import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Theme {
  name: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    border: string;
  };
}

export const themes: Record<string, Theme> = {
  darkPurple: {
    name: 'Dark Purple (Current)',
    colors: {
      primary: 'from-purple-900 via-blue-900 to-indigo-900',
      secondary: 'from-purple-600 to-blue-600',
      accent: 'purple-600',
      background: 'bg-white',
      surface: 'bg-gray-50',
      text: 'text-gray-900',
      textSecondary: 'text-gray-600',
      border: 'border-purple-600'
    }
  },
  darkIndigo: {
    name: 'Dark Indigo',
    colors: {
      primary: 'from-indigo-900 via-blue-900 to-slate-900',
      secondary: 'from-indigo-600 to-blue-600',
      accent: 'indigo-600',
      background: 'bg-slate-50',
      surface: 'bg-slate-100',
      text: 'text-slate-900',
      textSecondary: 'text-slate-600',
      border: 'border-indigo-600'
    }
  },
  darkTeal: {
    name: 'Dark Teal',
    colors: {
      primary: 'from-teal-900 via-cyan-900 to-blue-900',
      secondary: 'from-teal-600 to-cyan-600',
      accent: 'teal-600',
      background: 'bg-white',
      surface: 'bg-teal-50',
      text: 'text-gray-900',
      textSecondary: 'text-gray-600',
      border: 'border-teal-600'
    }
  },
  darkRose: {
    name: 'Dark Rose',
    colors: {
      primary: 'from-rose-900 via-pink-900 to-purple-900',
      secondary: 'from-rose-600 to-pink-600',
      accent: 'rose-600',
      background: 'bg-white',
      surface: 'bg-rose-50',
      text: 'text-gray-900',
      textSecondary: 'text-gray-600',
      border: 'border-rose-600'
    }
  },
  midnightBlue: {
    name: 'Midnight Blue',
    colors: {
      primary: 'from-blue-900 via-indigo-800 to-purple-800',
      secondary: 'from-blue-600 to-indigo-600',
      accent: 'blue-600',
      background: 'bg-blue-50',
      surface: 'bg-blue-100',
      text: 'text-blue-900',
      textSecondary: 'text-blue-700',
      border: 'border-blue-600'
    }
  },
  emeraldGreen: {
    name: 'Emerald Green',
    colors: {
      primary: 'from-emerald-800 via-green-800 to-teal-800',
      secondary: 'from-emerald-600 to-green-600',
      accent: 'emerald-600',
      background: 'bg-emerald-50',
      surface: 'bg-emerald-100',
      text: 'text-emerald-900',
      textSecondary: 'text-emerald-700',
      border: 'border-emerald-600'
    }
  },
  sunsetOrange: {
    name: 'Sunset Orange',
    colors: {
      primary: 'from-orange-700 via-red-700 to-pink-700',
      secondary: 'from-orange-500 to-red-500',
      accent: 'orange-600',
      background: 'bg-orange-50',
      surface: 'bg-orange-100',
      text: 'text-orange-900',
      textSecondary: 'text-orange-700',
      border: 'border-orange-600'
    }
  },
  lightPurple: {
    name: 'Light Purple',
    colors: {
      primary: 'from-purple-600 via-violet-600 to-indigo-600',
      secondary: 'from-purple-400 to-indigo-400',
      accent: 'purple-500',
      background: 'bg-purple-50',
      surface: 'bg-purple-100',
      text: 'text-purple-900',
      textSecondary: 'text-purple-700',
      border: 'border-purple-500'
    }
  },
  lightBlue: {
    name: 'Light Blue',
    colors: {
      primary: 'from-sky-600 via-blue-600 to-cyan-600',
      secondary: 'from-sky-400 to-blue-400',
      accent: 'sky-500',
      background: 'bg-sky-50',
      surface: 'bg-sky-100',
      text: 'text-sky-900',
      textSecondary: 'text-sky-700',
      border: 'border-sky-500'
    }
  }
};

interface ThemeContextType {
  currentTheme: Theme;
  themeName: string;
  setTheme: (themeName: string) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [themeName, setThemeName] = useState<string>('darkPurple');
  const currentTheme = themes[themeName];

  const setTheme = (name: string) => {
    setThemeName(name);
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, themeName, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
